export type UpdateSourceEntry = {
    source: string;
    timestamp: number;
};

export type UpdateSourceCount = {
    source: string;
    count: number;
};

const RING_CAPACITY = 100;

const ring: UpdateSourceEntry[] = [];
let nextWriteIndex = 0;
let writeCount = 0;

/**
 * Records that `source` is about to force a React re-render. Called on every forced re-render in
 * production, so it reuses the ring slots instead of allocating a new entry each time.
 */
export const recordUpdateSource = (source: string): void => {
    const existing = ring[nextWriteIndex];
    if (existing) {
        existing.source = source;
        existing.timestamp = Date.now();
    } else {
        ring[nextWriteIndex] = { source, timestamp: Date.now() };
    }
    nextWriteIndex = (nextWriteIndex + 1) % RING_CAPACITY;
    writeCount += 1;
};

/**
 * The recorded update sources, oldest first.
 */
export const getRecentUpdateSources = (): UpdateSourceEntry[] => {
    const oldestFirst: UpdateSourceEntry[] = [];
    const recorded = Math.min(writeCount, RING_CAPACITY);
    for (let offset = 0; offset < recorded; offset += 1) {
        const entry = ring[(nextWriteIndex - recorded + offset + RING_CAPACITY) % RING_CAPACITY];
        oldestFirst.push({ source: entry.source, timestamp: entry.timestamp });
    }
    return oldestFirst;
};

/**
 * How often each source appears in the ring buffer, most frequent first.
 */
export const getUpdateSourceCounts = (): UpdateSourceCount[] => {
    const counts = new Map<string, number>();
    for (const entry of getRecentUpdateSources()) {
        counts.set(entry.source, (counts.get(entry.source) || 0) + 1);
    }
    return [...counts.entries()]
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count);
};

export const clearUpdateSources = (): void => {
    ring.length = 0;
    nextWriteIndex = 0;
    writeCount = 0;
};
